import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["form", "input", "salt", "encryptedPrivateKey", "encryptedData", "template", "error", "displayArea"];

  connect() {
    // Only initialize salt buffer and input listeners if form targets exist
    if (this.hasSaltTarget) {
      this.saltBuffer = this.#encode(this.salt);
    }

    if (this.hasInputTarget && this.hasErrorTarget) {
      // Auto-clear errors when user starts typing
      this.inputTarget.addEventListener('input', () => {
        this.errorTarget.hidden = true;
      });
    }
  }

  async decrypt(event) {
    event.preventDefault();

    // Add loading state
    const submitButton = event.target.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Decrypting...';
    submitButton.disabled = true;

    try {
      const secretKey = await this.deriveSecretKey();
      let privateKey;

      privateKey = await crypto.subtle.unwrapKey(
        "jwk",
        this.#unpack(this.encryptedPrivateKey),
        secretKey,
        {
          name: "AES-GCM",
          iv: this.saltBuffer,
        },
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        false,
        ["decrypt"],
      );

      var decryptedData = await crypto.subtle.decrypt(
        {
          name: "RSA-OAEP",
        },
        privateKey,
        this.#unpack(this.encryptedData),
      );

      const decodedString = this.#decode(decryptedData);
      const decodedData = JSON.parse(decodedString);
      this.renderCard(decodedData);
      this.formTarget.hidden = true;

      // Optional: Show success message briefly
      const successDiv = document.createElement('div');
      successDiv.className = 'bg-green-50 text-green-600 px-3 py-2 rounded mb-4 text-center';
      successDiv.textContent = '✓ Card details unlocked successfully';
      this.formTarget.parentElement.insertBefore(successDiv, this.formTarget);
      setTimeout(() => successDiv.remove(), 3000);

    } catch (error) {
      console.error(error);
      this.errorTarget.hidden = false;
      this.errorTarget.textContent = "❌ Incorrect passphrase. Please check and try again.";
    } finally {
      // Reset button state
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  }

  async deriveSecretKey() {
    const derivedKeyMaterial = await crypto.subtle.importKey(
      "raw",
      this.#encode(this.passphrase),
      { name: "PBKDF2" },
      false, // extractable
      ["deriveKey"],
    );

    return await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: this.saltBuffer,
        // https://cheatsheetseries.owasp.org/cheatsheets/Password_Storage_Cheat_Sheet.html#pbkdf2
        iterations: 600000,
        hash: "SHA-256",
      },
      derivedKeyMaterial,
      { name: "AES-GCM", length: 256 },
      true, // extractable
      ["wrapKey", "unwrapKey"],
    );
  }

  renderCard(decodedData) {
    const clone = this.templateTarget.content.cloneNode(true);

    // Try to find display area target within this controller's scope
    let targetContainer = null;
    if (this.hasDisplayAreaTarget) {
      targetContainer = this.displayAreaTarget;
    } else {
      // Fallback: look for display area within this controller's element
      targetContainer = this.element.querySelector('[data-display-area]');
    }

    if (decodedData.number.length == 16) {
      clone.querySelector("[data-card-section-1]").textContent = decodedData.number.slice(0, 4);
      clone.querySelector("[data-card-section-2]").textContent = decodedData.number.slice(4, 8);
      clone.querySelector("[data-card-section-3]").textContent = decodedData.number.slice(8, 12);
      clone.querySelector("[data-card-section-4]").textContent = decodedData.number.slice(12, 16);
    } else if (decodedData.number.length == 15) {
      clone.querySelector("[data-card-section-1]").textContent = decodedData.number.slice(0, 4);
      clone.querySelector("[data-card-section-2]").textContent = decodedData.number.slice(4, 10);
      clone.querySelector("[data-card-section-3]").textContent = decodedData.number.slice(10, 15);
    } else if (decodedData.number.length == 19) {
      clone.querySelector("[data-card-section-1]").textContent = decodedData.number.slice(0, 4);
      clone.querySelector("[data-card-section-2]").textContent = decodedData.number.slice(4, 8);
      clone.querySelector("[data-card-section-3]").textContent = decodedData.number.slice(8, 12);
      clone.querySelector("[data-card-section-4]").textContent = decodedData.number.slice(12, 19);
    } else {
      decodedData.number.match(/.{1,4}/g).forEach((section, i) => {
        clone.querySelector(`[data-card-section-${i + 1}]`).textContent = section;
      });
    }

    const numberDisplay = clone.querySelector("[data-number-display]");
    if (numberDisplay) numberDisplay.textContent = decodedData.number;

    const expiration = `${decodedData.expiration_month.padStart(2, "0")}/${decodedData.expiration_year}`;
    const expirationDisplay = clone.querySelectorAll("[data-expiration-display]");
    const securityCodeDisplay = clone.querySelectorAll("[data-security-code-display]");
    const holderDisplay = clone.querySelectorAll("[data-holder-name]");

    expirationDisplay.forEach(field => {
      field.textContent = expiration;
    });

    securityCodeDisplay.forEach(field => {
      field.textContent = decodedData.security_code;
    });

    holderDisplay.forEach(field => {
      field.textContent = decodedData.holder;
    });

    const billingAddressDisplay = clone.querySelector("[data-billing-address-display]");
    if (billingAddressDisplay) billingAddressDisplay.textContent = decodedData.billing_address;

    const notesDisplay = clone.querySelector("[data-notes-display]");
    if (notesDisplay) notesDisplay.textContent = decodedData.notes;

    const cardType = clone.querySelector("[data-card-type]");
    if (cardType) {
      if(decodedData.lease_request_card_type == 'any') {
        cardType.textContent = decodedData.lease_response_actual_card_type;
      } else {
        cardType.textContent = decodedData.lease_request_card_type;
      }
    }

    const brandToShow = decodedData.lease_request_card_brand === 'any'
    ? decodedData.lease_response_actual_card_brand
    : decodedData.lease_request_card_brand;

    const cardBrand = clone.querySelector(`[data-card-brand='${brandToShow}']`);
    if (cardBrand) cardBrand.classList.remove("hidden");

    if (targetContainer) {
      // Clear existing content and replace with unlocked content
      targetContainer.innerHTML = '';
      targetContainer.appendChild(clone);
    } else {
      this.element.append(clone);
    }
  }

  #encode(data) {
    // https://developer.mozilla.org/en-US/docs/Web/API/TextEncoder
    const encoder = new TextEncoder();

    return encoder.encode(data);
  }

  #decode(bytestream) {
    // https://developer.mozilla.org/en-US/docs/Web/API/TextDecoder
    const decoder = new TextDecoder();

    return decoder.decode(bytestream);
  }

  #unpack(packed) {
    // https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
    const string = window.atob(packed);
    const buffer = new ArrayBuffer(string.length);
    const bufferView = new Uint8Array(buffer);

    for (let i = 0; i < string.length; i++) {
      bufferView[i] = string.charCodeAt(i);
    }

    return buffer;
  }

  get salt() {
    return this.hasSaltTarget ? this.saltTarget.value : null;
  }
  get encryptedPrivateKey() {
    return this.hasEncryptedPrivateKeyTarget ? this.encryptedPrivateKeyTarget.value : null;
  }
  get encryptedData() {
    return this.hasEncryptedDataTarget ? this.encryptedDataTarget.value : null;
  }
  get passphrase() {
    return this.hasInputTarget ? this.inputTarget.value : null;
  }
}
